﻿import { ModelTargetRow } from "@/ServerTypes/Data/ModelTargetRow";
import { CascadedWidgetLink, ComboboxEditor, ComboboxItem, Decorators, ScriptData, Widget, endsWith, htmlEncode, isEmptyOrNull, toId } from "@serenity-is/corelib";

export interface ModelTargetEditorOptions {
    typeCode: string;
    level: number;
    cascadeFrom: string;
}

@Decorators.registerEditor("Matrix.Data.ModelTargetEditor")
export class ModelTargetEditor extends ComboboxEditor<ModelTargetEditorOptions, ModelTargetRow> {

    private _cascadeValue: ModelTargetRow;
    private disposeChange: any;

    constructor(props?: { element?: any } & ModelTargetEditorOptions) {
        super(props);

        this.setCascadeFrom(this.options.cascadeFrom);
        this.updateItems();
        this.disposeChange = ScriptData.bindToChange(ModelTargetRow.lookupKey, () => {
            this.updateItems();
        });
    }

    destroy(): void {
        this.disposeChange?.();
        super.destroy();
    }

    private getCodeValue(item: ModelTargetRow, level?: number) {
        level = level || this.options.level || 1;

        return item['Code' + level];
    }

    private getNameValue(item: ModelTargetRow, level?: number) {
        level = level || this.options.level || 1;

        return item['Name' + level];
    }

    protected getItems() {
        return this.cascadeItems(ModelTargetRow.getLookup().items);
    }

    protected getItemText(item: ModelTargetRow) {
        var code = this.getCodeValue(item);
        var name = this.getNameValue(item);
        if (code == null)
            return "";

        return code.toString() + ' - ' + (name ?? '').toString();
    }

    public updateItems() {
        this.clearItems();

        var items = this.getItems();

        for (var item of items) {
            var txt = this.getItemText(item);
            var idValue = this.getCodeValue(item);
            var id = (idValue == null ? '' : idValue.toString());
            this.addItem({
                id: id,
                text: txt,
                source: item
            });
        }
    }

    protected cascadeItems(items: ModelTargetRow[]) {

        if (!this.options.typeCode)
            return [];

        var c = this.options.typeCode;
        var level = this.options.level || 1;
        items = items.filter(x => x.ModelCode === c && x.Level === level);

        if (level > 1) {
            var val = this._cascadeValue;
            if (!val) {
                return [];
            }

            return items.filter(x => {
                for (var l = 1; l < level; l++) {
                    if (this.getCodeValue(x, l) !== this.getCodeValue(val, l))
                        return false;
                }

                return true;
            });
        }
        else
            return items;
    }

    protected getCascadeFromValue(parent: Widget<any>) {
        var p = parent as ModelTargetEditor;
        var v = p.value;
        if (!v)
            return null;

        var x = p.itemById[toId(v)];
        if (!x)
            return null;

        return x.source;
    }

    protected cascadeLink: CascadedWidgetLink<Widget<any>>;

    protected setCascadeFrom(value: string) {

        if (isEmptyOrNull(value)) {
            if (this.cascadeLink != null) {
                this.cascadeLink.set_parentID(null);
                this.cascadeLink = null;
            }
            this.options.cascadeFrom = null;
            return;
        }

        this.cascadeLink = new CascadedWidgetLink<Widget<any>>(
            Widget, this, p => {
                this.setCascadeValue(this.getCascadeFromValue(p));
            });

        var v = value;
        var n = this.element.attr('name');
        var id = this.element.attr('id');
        if (endsWith(id, '_' + n)) {
            v = id.substr(0, id.length - n.length) + v;
        }

        this.cascadeLink.set_parentID(v);
        this.options.cascadeFrom = value;
    }

    protected getComboboxOptions() {
        var opt = super.getComboboxOptions();
        
        opt.allowClear = true;

        opt.formatResult = (item: ComboboxItem) => {
            if (!item || !item.source)
                return "";

            var id = htmlEncode(item.id);
            var name = htmlEncode(this.getNameValue(item.source));
            return `<div class="row" title="${id} - ${name}">
                        <div class="col-4" style="overflow: hidden"><b>${id}</b></div>
                        <div class="col-8"><span style="white-space: nowrap;">${name}</span></div></div>`;
        };

        opt.formatSelection = (item: ComboboxItem) => {
            if (!item || !item.source)
                return null;

            var id = htmlEncode(item.id);
            var name = htmlEncode(this.getNameValue(item.source));
            return `<span title="${id} - ${name}"><b>${id}</b> - <span style="white-space: nowrap;">${name}</span></span>`;
        };

        return opt;
    }

    protected get_cascadeFrom(): string {
        return this.options.cascadeFrom;
    }

    get cascadeFrom(): string {
        return this.get_cascadeFrom();
    }

    protected set_cascadeFrom(value: string) {
        if (value !== this.options.cascadeFrom) {
            this.setCascadeFrom(value);
            this.updateItems();
        }
    }

    set cascadeFrom(value: string) {
        this.set_cascadeFrom(value);
    }

    protected setCascadeValue(value: any) {
        if (this._cascadeValue !== value) {
            this._cascadeValue = value;
            this.set_value(null);
            this.updateItems();
        }
    }
}
